import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { ArrowLeft, Check, Zap, LogOut, Sparkles } from "lucide-react";
import { useState } from "react";
import usePageMeta from "../hooks/usePageMeta";

const FEATURES = {
  free: [
    { text: "Up to 25 applications", included: true },
    { text: "Table view", included: true },
    { text: "CSV export", included: true },
    { text: "Follow-up reminders", included: true },
    { text: "Kanban board view", included: false },
    { text: "Analytics & funnel", included: false },
    { text: "Excel & PDF exports", included: false },
    { text: "Unlimited applications", included: false },
  ],
  pro: [
    { text: "Unlimited applications", included: true },
    { text: "Table + Kanban board views", included: true },
    { text: "Analytics dashboard + funnel", included: true },
    { text: "CSV, Excel & PDF exports", included: true },
    { text: "Follow-up reminders + insights", included: true },
    { text: "Interview stage tracking", included: true },
    { text: "Priority support", included: true },
    { text: "All future Pro features", included: true },
  ],
};

function PlanCard({ badge, name, price, sub, cta, onCta, loading, features, highlight, testId }) {
  return (
    <div
      className={`rounded-3xl border p-8 relative ${
        highlight
          ? "bg-[#1A362D] text-[#F4F1EE] border-[#1A362D]"
          : "bg-white border-[#E5E1DB] text-[#1A362D]"
      }`}
      data-testid={testId}
    >
      {badge && (
        <span className={`absolute -top-3 left-8 rounded-full text-xs tracking-[0.2em] uppercase font-semibold px-3 py-1 ${
          highlight ? "bg-[#D99C3D] text-[#1A362D]" : "bg-[#E2F0E7] text-[#1A362D]"
        }`}>
          {badge}
        </span>
      )}
      <p className={`text-xs tracking-[0.2em] uppercase font-semibold ${highlight ? "opacity-70" : "text-[#5C6B64]"}`}>{name}</p>
      <div className="mt-4 flex items-baseline gap-1">
        <span className="font-display text-5xl">{price}</span>
        {sub && <span className={`text-sm ${highlight ? "opacity-70" : "text-[#5C6B64]"}`}>{sub}</span>}
      </div>
      <Button
        onClick={onCta}
        disabled={loading}
        className={`w-full mt-6 h-12 rounded-full text-base ${
          highlight
            ? "bg-[#D99C3D] hover:bg-[#C48A2E] text-[#1A362D] font-semibold"
            : "bg-[#1A362D] hover:bg-[#2C4A40] text-white"
        }`}
        data-testid={`${testId}-cta`}
      >
        {loading ? "Redirecting to Stripe…" : cta}
      </Button>
      <ul className="mt-8 space-y-3">
        {features.map((f, i) => (
          <li key={i} className={`flex items-start gap-2.5 text-sm ${!f.included ? "opacity-40 line-through" : ""}`}>
            <Check className={`w-4 h-4 mt-0.5 shrink-0 ${highlight ? "text-[#D99C3D]" : "text-[#1A362D]"}`} />
            <span>{f.text}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Upgrade() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);
  usePageMeta(
    "Upgrade to Trackr Pro — $6/mo or $19 lifetime",
    "Unlock Kanban board, analytics, Excel & PDF exports, and AI features. Trackr Pro from $6/mo or $19 one-time."
  );

  const goToCheckout = async (lookup_key) => {
    if (!user) {
      navigate("/login");
      return;
    }
    setLoading(lookup_key);
    try {
      const origin_url = window.location.origin;
      const { data } = await api.post("/payments/checkout", { lookup_key, origin_url });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setLoading(null);
    }
  };

  const handleLogout = async () => { await logout(); navigate("/"); };

  const isPro = user?.tier === "pro";

  return (
    <div className="min-h-screen bg-[#F4F1EE]">
      <header className="glass sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to={user ? "/dashboard" : "/"} className="inline-flex items-center gap-2 text-[#1A362D]" data-testid="back-link">
            <ArrowLeft className="w-4 h-4" /> <span className="font-display text-lg">Trackr</span>
          </Link>
          {user && (
            <div className="flex items-center gap-3">
              <span className="hidden sm:block text-sm text-[#5C6B64]">{user.email}</span>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="rounded-full" data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-1.5" /> Sign out
              </Button>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-12">
        <div className="max-w-2xl">
          <p className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#5C6B64] font-semibold">
            <Zap className="w-3.5 h-3.5" /> Upgrade to Trackr Pro
          </p>
          <h1 className="font-display text-5xl sm:text-6xl mt-3 text-[#1A362D] leading-tight">
            Serious search.<br />Serious tools.
          </h1>
          <p className="text-lg text-[#5C6B64] mt-4 leading-relaxed">
            Unlimited applications, Kanban board, analytics, and every export. Track like you mean it.
          </p>
        </div>

        {isPro && (
          <div className="mt-8 rounded-2xl bg-[#E2F0E7] border border-[#1A362D]/10 p-5 flex items-start gap-3" data-testid="already-pro-banner">
            <Sparkles className="w-5 h-5 text-[#1A362D] shrink-0 mt-0.5" />
            <p className="text-[#1A362D]">You're already on Trackr Pro. Thanks for supporting us.</p>
          </div>
        )}

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <PlanCard
            testId="plan-free"
            name="Free"
            price="$0"
            sub="/ forever"
            cta="You're on Free"
            onCta={() => navigate("/dashboard")}
            features={FEATURES.free}
          />
          <PlanCard
            testId="plan-monthly"
            badge="Most flexible"
            name="Pro · Monthly"
            price="$6"
            sub="/ month"
            cta={isPro ? "Already Pro" : "Start monthly"}
            onCta={() => !isPro && goToCheckout("pro_monthly")}
            loading={loading === "pro_monthly"}
            features={FEATURES.pro}
          />
          <PlanCard
            testId="plan-lifetime"
            badge="Best value"
            highlight
            name="Pro · Lifetime"
            price="$19"
            sub="one-time"
            cta={isPro ? "Already Pro" : "Buy lifetime"}
            onCta={() => !isPro && goToCheckout("pro_lifetime")}
            loading={loading === "pro_lifetime"}
            features={FEATURES.pro}
          />
        </div>

        <p className="text-xs text-[#5C6B64] mt-8 text-center">
          Secure checkout by Stripe · Cancel anytime · Test mode uses card 4242 4242 4242 4242
        </p>
      </main>
    </div>
  );
}
